import React, { useCallback, useEffect, useMemo, useRef } from 'react';
import type { Block, Document, TableRow } from '@contentful/rich-text-types';
import { BLOCKS } from '@contentful/rich-text-types';
import { z } from 'zod';
import { CmsImageSchema, CmsLinkSchema, CmsRichTextSchema, CmsShortTextSchema } from '$cms/types';
import type { SchemaType } from '$util/schema';
import { useModalQueue } from '$util/hooks/useModalQueue';
import type { ContentComponent } from '$cms/ContentComponent';
import { HeadingLevels } from '$src-components/atoms/HeadingLevels';
import { Image } from '$components/basic/Image';
import { Link } from '$quantum/atoms/Link';
import { findTextNode, renderRichTextContent } from '@/cms/richTextContentHelpers';
import { Button } from '@/components/atoms/Button';
import { dispatchCloseQueuedModalEvent } from '@/components/molecules/Modal';
import { renderRichTextContentOptions } from './helpers';
import { HeaderCell } from './HeaderCell';

import styles from './index.module.scss';

const findTableRow = ({ content }: Document | Block): TableRow | undefined => {
  if (!content) {
    return undefined;
  }

  // eslint-disable-next-line
  for (const node of content) {
    if (node.nodeType === BLOCKS.TABLE_ROW) {
      return node as TableRow;
    }
    return findTableRow(node as Block);
  }

  return undefined;
};

const RichTextTableModalSchema = {
  translatedEntities: z.object({
    heading: CmsShortTextSchema.optional(),
    content: CmsRichTextSchema,
    closeButtonText: CmsShortTextSchema.optional(),
    ctaTitle: CmsShortTextSchema.optional(),
    ctaLink: CmsLinkSchema.optional(),
    ctaImage: CmsImageSchema.optional(),
  }),
};

export type RichTextTableModalTranslatedEntities = SchemaType<
  (typeof RichTextTableModalSchema)['translatedEntities']
>;

/**
 * Component which renders Table with data from Contentful <br />
 * https://www.notion.so/fixter/RichTextTableModal-776cd2a1c9a942c0b4bface0cf1d733c
 */
export const RichTextTableModal: ContentComponent<RichTextTableModalTranslatedEntities> =
  function RichTextTableModal({ translatedEntities, id }): null {
    const { content, heading, closeButtonText, ctaImage, ctaLink, ctaTitle } = translatedEntities;

    const tableWrapperRef = useRef<HTMLDivElement>(null);
    const tableHeaderCellRef = useRef<HTMLDivElement>(null);

    const tableHeaderValues = useMemo(() => {
      const tr = findTableRow(content.content);
      const values = tr?.content.map((c) => {
        try {
          const value = findTextNode(c) || '';
          const parsed = JSON.parse(value);

          return parsed;
        } catch (error) {
          return null;
        }
      });

      return values;
    }, [content.content]);

    const renderFooter = useCallback(
      (closeModal: () => void) => {
        if (closeButtonText) {
          return (
            <Button onClick={closeModal} className="close-button">
              {closeButtonText?.value}
            </Button>
          );
        }
        if (ctaTitle || ctaLink) {
          return (
            <div className={styles.cta}>
              {ctaImage && (
                <div
                  style={{ width: ctaImage.width, height: ctaImage.height }}
                  className={styles.ctaImageWrapper}
                >
                  <Image
                    src={ctaImage.url}
                    alt={ctaImage.alt}
                    width={ctaImage.width}
                    height={ctaImage.height}
                    provider="cloudinary"
                  />
                </div>
              )}
              <div>
                {ctaTitle && <p className={styles.ctaTitle}>{ctaTitle?.value}</p>}
                {ctaLink && (
                  <Link className={styles.link} type="small" href={ctaLink.url}>
                    {ctaLink.text}
                  </Link>
                )}
              </div>
            </div>
          );
        }
        return null;
      },
      [ctaImage, ctaLink, ctaTitle, closeButtonText]
    );

    const [, queueModal] = useModalQueue();

    const MODAL_TRIGGER_FRAGMENT_PATTERN = /#open-modal=/;

    const openModal = useCallback(() => {
      queueModal([
        // eslint-disable-next-line react/jsx-key
        <div
          className="inner-wrapper"
          style={{
            // @ts-ignore Pass prop to styles
            '--columns': tableHeaderValues?.length || 0,
          }}
        >
          {heading && (
            <HeadingLevels semanticLevel={2} className={styles.heading}>
              {heading?.value}
            </HeadingLevels>
          )}
          <div className={styles.tableWrapper} ref={tableWrapperRef}>
            {tableHeaderValues && (
              <div className={styles.tableHeaderDuplicate} ref={tableHeaderCellRef}>
                {tableHeaderValues.slice(1).map((node, i) => (
                  // eslint-disable-next-line react/no-array-index-key
                  <HeaderCell key={i} className={styles.headerCell} {...node} />
                ))}
              </div>
            )}
            {content && renderRichTextContent(content.content, { ...renderRichTextContentOptions })}
          </div>
          {renderFooter(() => {
            if (tableWrapperRef.current) {
              dispatchCloseQueuedModalEvent(tableWrapperRef.current);
            }
          })}
        </div>,
        'modal-drawer',
      ]);
    }, [content, heading, queueModal, renderFooter, tableHeaderValues]);

    useEffect(() => {
      /**
       * @NOTE here's an example of how this same logic can be written in true JS async with the immutable style
       *
       * contrast the readability of this with the synchronous style below
       *
       * Also, consider how much more modular and testable this code is; single responsibility, extracted to modules, reuse etc.
       *
       * When core client side performance issues are addressed, this type of coding style becomes the
       * type of microoptimisation that can make a difference to not only the main thread work, because of how terse it is,
       * the elimination of code branches (condition statements), no use of vars/const/lets (improved memory processing) etc, but
       * also through improved bundling - it opens up opportunities for lazy and on demand loading of modules. On top of the fact
       * that by using the Promise/then syntax, we get the benefits of true concurrency in our code.
       *
       * */
      // const shouldWeOpenModal = () =>
      //   Promise.resolve(
      //     MODAL_TRIGGER_FRAGMENT_PATTERN.test(window.location.hash) &&
      //       window.location.hash.replace(MODAL_TRIGGER_FRAGMENT_PATTERN, '') === id
      //   );
      // const isUrlHashEmpty = () =>
      //   window.location.hash === '' ? Promise.resolve(true) : Promise.reject(false);

      // const handleHashChange = () => {
      //   shouldWeOpenModal()
      //     .then(
      //         (shouldOpen) => shouldOpen && openModal()
      //     );

      //   isUrlHashEmpty()
      //     .then(
      //       () => tableWrapperRef.current && dispatchCloseQueuedModalEvent(tableWrapperRef.current);
      //     );
      // };

      // shouldWeOpenModal()
      //   .then(
      //     (shouldOpen) => shouldOpen && openModal()
      //   );

      // sync style
      const shouldOpenModal = () => {
        const openAModal = MODAL_TRIGGER_FRAGMENT_PATTERN.test(window.location.hash);

        if (openAModal) {
          // get the modal's ID
          const modalId = window.location.hash.replace(MODAL_TRIGGER_FRAGMENT_PATTERN, '');

          return modalId === id;
        }

        return false;
      };

      const handleHashChange = () => {
        // has open modal id
        if (shouldOpenModal()) {
          openModal();
        }
        if (window.location.hash === '') {
          if (tableWrapperRef.current) {
            dispatchCloseQueuedModalEvent(tableWrapperRef.current);
          }
        }
      };
      // // open the modal if the page is loaded with a modal hash
      if (shouldOpenModal()) {
        openModal();
      }

      window.addEventListener('hashchange', handleHashChange);

      return () => window.removeEventListener('hashchange', handleHashChange);
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    return null;
  };
RichTextTableModal.displayName = 'RichTextTableModal';
RichTextTableModal.schema = RichTextTableModalSchema;
